import { Avatar, Box, Card, CardContent, CardMedia, Chip, Typography } from '@mui/material';
import { Calendar } from 'lucide-react';
import dayjs from 'dayjs';
import type { StrapiArticle } from './types';

interface ArticleCardProps {
  article: StrapiArticle;
  onClick?: () => void;
}

export const ArticleCard = ({ article, onClick }: ArticleCardProps) => {
  const dateToDisplay = article.displayPublishDate || article.publishedAt;
  const formattedDate = dayjs(dateToDisplay).format('YYYY-MM-DD');

  return (
    <Card
      sx={{
        display: 'flex',
        minWidth: { xs: 0, sm: '430px' },
        maxWidth: '100%',
        borderRadius: '16px',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick
          ? {
              boxShadow: 3,
            }
          : {},
      }}
      onClick={onClick}
    >
      {article.cover && (
        <CardMedia
          component="img"
          sx={{
            width: 440,
            height: 234,
            objectFit: 'cover',
            flexShrink: 0,
            display: { xs: 'none', lg: 'block' },
          }}
          image={article.cover.url}
          alt={article.cover.alternativeText || article.title}
        />
      )}
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          padding: '16px',
          flexGrow: 1,
          minWidth: 0,
        }}
      >
        {/* Date and Category Badge Row */}
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            <Calendar size={24} />
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                whiteSpace: 'nowrap',
              }}
            >
              {formattedDate}
            </Typography>
          </Box>
          {article.category && (
            <Chip
              label={article.category.name}
              variant="outlined"
              color="primary"
              size="small"
            />
          )}
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {article.title}
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {article.description}
        </Typography>

        {/* Author */}
        {article.author && (
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
              marginTop: 'auto',
            }}
          >
            <Avatar
              src={article.author.avatar?.url}
              alt={article.author.name}
              sx={{
                width: 40,
                height: 40,
              }}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="subtitle1">
                {article.author.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {article.author.title}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
